import { Injectable } from '@angular/core';
import {
    AnalysisInfo,
    RecordReference,
    Report,
    ReportCenterLabResult,
    ReportCenterRecordFilter,
    ReportCenterRecordResult,
    ReportData,
} from '@delab/ngx-gen-ts-ixserv/models';
import { DelabNgxSingleReportServiceDefinition, ReportWithPdf } from '@delab/ngx-ixconnect-components';
import { map, Observable, of } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { RecordApiService } from '../../.api-client/services/record-api.service';
import { ReportCenterApiService } from '../../.api-client/services/report-center-api.service';
import { ReportsApiService } from '../../.api-client/services/reports-api.service';
import { tapError } from '../shared/rxjs-operators/tap-error.operator';

@Injectable({ providedIn: 'root' })
export class ReportService implements DelabNgxSingleReportServiceDefinition {
    constructor(
        private _reportCenterApiService: ReportCenterApiService,
        private _reportsApiService: ReportsApiService,
        private _recordApiService: RecordApiService,
    ) {}

    public getReportWithPdf(recId: number): Observable<ReportWithPdf> {
        return this._reportCenterApiService.reportCenterReportWithPdf({ 'x-trace-id': uuidv4(), recID: recId }).pipe(
            map((reportWithPdf) => {
                return {
                    report: new ReportData(reportWithPdf.report),
                    pdf: reportWithPdf.pdf,
                };
            }),
        );
    }

    public getAnalysis(lrsID: number): Observable<AnalysisInfo | undefined> {
        if (!lrsID) {
            return of(undefined);
        }
        return this._reportCenterApiService.reportCenterAnalysis({ 'x-trace-id': uuidv4(), lrsID }).pipe(
            map((value) => {
                if (typeof value === 'undefined') {
                    throw Error();
                }
                return new AnalysisInfo(value);
            }),
        );
    }

    public getLabresult(perID: number, casID: number, lrsID: number): Observable<ReportCenterLabResult | undefined> {
        if (!lrsID || !perID || !casID) {
            return of(undefined);
        }
        return this._reportCenterApiService.reportCenterLabresult({ casID, perID, 'x-trace-id': uuidv4(), lrsID }).pipe(
            map((value) => {
                if (typeof value === 'undefined') {
                    throw Error();
                }
                return new ReportCenterLabResult(value);
            }),
        );
    }

    public markReadImplicit(recId: number): Observable<void> {
        return this._reportCenterApiService
            .reportCenterMarkReportReadImplicit({ 'x-trace-id': uuidv4(), recID: recId })
            .pipe(tapError((error) => console.error('Error marking record as read:', error)));
    }

    getRecordReferences(recID: number): Observable<RecordReference[]> {
        return this._recordApiService.recordGetRecordReference({ 'x-trace-id': uuidv4(), recID }).pipe(
            map((value) => {
                return value.references.map((entry) => new RecordReference(entry));
            }),
        );
    }

    getRecords(reportCenterRecordFilter: ReportCenterRecordFilter): Observable<ReportCenterRecordResult | undefined> {
        return this._reportCenterApiService.reportCenterRecords({ 'x-trace-id': uuidv4(), body: reportCenterRecordFilter.toJSON() }).pipe(
            map((value) => {
                if (typeof value === 'undefined') {
                    throw Error();
                }
                return new ReportCenterRecordResult(value);
            }),
        );
    }

    getReports(rmhIDs: number[]): Observable<Report | undefined> {
        return this._reportsApiService.reportsGetReports({ 'x-trace-id': uuidv4(), body: rmhIDs }).pipe(
            map((value) => {
                return new Report(value);
            }),
        );
    }

    getOpenPdfUrl(docId: string): string {
        return '/api/reports/pdf/' + docId;
    }
}
